import React from 'react';
import { LogOut, User } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getTranslation } from '../utils/translations';
import type { Language } from '../types';

interface AuthButtonProps {
  isDark: boolean;
  language: Language;
}

export function AuthButton({ isDark, language }: AuthButtonProps) {
  const { user, loading, signInWithGoogle, signOut } = useAuth();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
        isDark ? 'bg-gray-700' : 'bg-gray-100'
      }`}>
        <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (user) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {user.photoURL ? (
            <img 
              src={user.photoURL} 
              alt={user.displayName || 'User'} 
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isDark ? 'bg-gray-600' : 'bg-gray-200'
            }`}>
              <User className="w-4 h-4" />
            </div>
          )}
          <div className="hidden sm:block">
            <p className="text-sm font-medium">{user.displayName}</p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {user.email}
            </p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            isDark 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">
            {getTranslation('auth.signOut', language)}
          </span>
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleSignIn}
      className={`flex items-center justify-center p-2 rounded-lg transition-colors border ${
        isDark 
          ? 'bg-white hover:bg-gray-50 border-gray-300' 
          : 'bg-white hover:bg-gray-50 border-gray-300'
      } shadow-sm`}
      title="Sign in with Google"
    >
      {/* Google G Logo */}
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    </button>
  );
}